import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppAuth } from "./auth";
import { Loading } from "./components/ui";
import { Shell } from "./components/Shell";
import { NotAuthorized } from "./components/NotAuthorized";
import { SignIn } from "./pages/SignIn";
import { CreateAccount } from "./pages/CreateAccount";
import { ResetPassword } from "./pages/ResetPassword";
import { CheckEmail } from "./pages/CheckEmail";
import { Schedule } from "./pages/Schedule";
import { VisitScreen } from "./pages/VisitScreen";
import { Patients } from "./pages/Patients";
import { PatientDetail } from "./pages/PatientDetail";
import { Messages } from "./pages/Messages";
import { Availability } from "./pages/Availability";
import { Profile } from "./pages/Profile";

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, authz } = useAppAuth();
  // Gate order matters: session loading → signed-in check → SERVER authorization. The
  // shell renders only once the server confirms this account is authorized for the portal — never
  // on client-side sign-in alone. "checking" holds (no shell flash); "denied" → rejection wall.
  if (!isLoaded) return <Loading label="Checking your session…" />;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  if (authz === "checking") return <Loading label="Verifying access…" />;
  if (authz === "denied") return <NotAuthorized />;
  return <>{children}</>;
}

export function App() {
  return (
    <div className="doctor-app">
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/create-account" element={<CreateAccount />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route index element={<Schedule />} />
          <Route path="visit/:id" element={<VisitScreen />} />
          <Route path="patients" element={<Patients />} />
          <Route path="patients/:id" element={<PatientDetail />} />
          <Route path="messages" element={<Messages />} />
          <Route path="availability" element={<Availability />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
